import { useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, Select, MenuItem, FormControl, InputLabel,
    CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTheme } from '@mui/material/styles';
import { deleteSuccessRequest, currentUserName } from '../../../services/successRequestService';
import { normaliseRequest } from '../../../services/workService';
import { useConfirm } from '../../../hooks/useConfirm';
import WorkActions from '../../Work/WorkActions';
import { WorkItemRow } from '../../Work/workUi';
import { Tip, Explain, ExplainToggle } from '../../Work/explain';
import X from '../../Work/explainText';
import { REQUEST_STATUSES, REQUEST_PRIORITIES, STATUS_ORDER } from './constants';

/**
 * Implementation requests as work rows.
 *
 * Until 2026-09-22 this was a data grid with its own columns and two buttons,
 * which made a request look like a different kind of thing from the same
 * request on the Work overview or a client's Work tab. It is the same row now:
 * the same chips, people and six actions, plus the two things only this page
 * can do — open the request's full form (details, attachments, resolution)
 * and delete it.
 */

const attachmentCount = (row) => {
    try {
        const arr = row.Attachments ? JSON.parse(row.Attachments) : [];
        return Array.isArray(arr) ? arr.length : 0;
    } catch { return 0; }
};

const RequestsList = ({ requests = [], loading = false, onRefresh, onOpenRequest }) => {
    const [dialogs, dialogUi] = useConfirm();
    const theme = useTheme();
    const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [rowError, setRowError] = useState(null);

    const rows = useMemo(() => requests
        .filter((r) => (!statusFilter || r.Status === statusFilter)
            && (!priorityFilter || r.Priority === priorityFilter))
        // Open work first, in the board's own status order, newest inside.
        .sort((a, b) => (STATUS_ORDER[a.Status] ?? 9) - (STATUS_ORDER[b.Status] ?? 9)
            || String(b.CreatedOn).localeCompare(String(a.CreatedOn))),
    [requests, statusFilter, priorityFilter]);

    const handleDelete = useCallback(async (row, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!(await dialogs.confirm({ title: `Delete request "${row.Title}"?`, message: 'It will be marked deleted.', confirmLabel: 'Delete', danger: true }))) return;
        setDeletingId(row.NexusSuccessRequest_Id);
        setRowError(null);
        try {
            const who = await currentUserName();
            await deleteSuccessRequest(row.NexusSuccessRequest_Id, who);
            await onRefresh?.();
        } catch (err) {
            setRowError(err.message);
        } finally {
            setDeletingId(null);
        }
    }, [onRefresh, dialogs]);

    return (
        <Box>
            {dialogUi}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Explain text={X.reqStatusFilter}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {REQUEST_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Explain>
                <Explain text={X.reqPriorityFilter}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Priority</InputLabel>
                        <Select label="Priority" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {REQUEST_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Explain>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {rows.length} of {requests.length}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <ExplainToggle />
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={() => onRefresh?.()} disabled={loading}>
                            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {rowError && <Typography sx={{ color: '#c62828', fontSize: 13, mb: 1 }}>{rowError}</Typography>}

            <Box sx={{ border: '1px solid var(--color-divider)', borderRadius: 1.5, px: 1.5, py: 0.5,
                backgroundColor: 'var(--color-bg-paper)' }}>
                {rows.length === 0 && !loading && (
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)', py: 3, textAlign: 'center' }}>
                        No requests match. Click “New Request” to add one.
                    </Typography>
                )}
                {rows.map((r) => {
                    const item = normaliseRequest(r);
                    const files = attachmentCount(r);
                    return (
                        <WorkItemRow key={item.id} item={item} mode={mode}
                            actions={<WorkActions item={item} onChanged={() => onRefresh?.()} />}
                            trailing={(
                                <>
                                    {files > 0 && (
                                        <Tip title={`${files} attachment${files === 1 ? '' : 's'}`}
                                            explain="Files attached to this request (Attachments column). Open the request to download them.">
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center',
                                                fontSize: 11, color: 'var(--color-text-secondary)', gap: '2px', px: 0.4 }}>
                                                <AttachFileIcon sx={{ fontSize: 14 }} />{files}
                                            </Box>
                                        </Tip>
                                    )}
                                    <Tip title="Open the full request" explain={X.reqOpen}>
                                        <IconButton size="small" aria-label="Open the full request"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenRequest?.(r); }}>
                                            <OpenInFullIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tip>
                                    <Tip title="Delete" explain={X.reqDelete}>
                                        <IconButton size="small" aria-label="Delete" onClick={(e) => handleDelete(r, e)}
                                            disabled={deletingId === r.NexusSuccessRequest_Id}>
                                            {deletingId === r.NexusSuccessRequest_Id
                                                ? <CircularProgress size={14} />
                                                : <DeleteIcon sx={{ fontSize: 16, color: '#c62828' }} />}
                                        </IconButton>
                                    </Tip>
                                </>
                            )} />
                    );
                })}
            </Box>
        </Box>
    );
};

export default RequestsList;
